import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { toast } from 'sonner';
import { Plus, Trash2, Search, Package, CheckCircle, X, Settings2, Loader2, ScanLine, Barcode, Edit2 } from 'lucide-react';
import Pagination from '../components/Pagination';
import api from '../services/api';

export default function RawMaterials() {
    const [tab, setTab] = useState('stock');
    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="raw-materials-page">
                <h1 className="page-title">Hammadde</h1>
                <div className="flex gap-1 border-b border-gray-100 pb-px">
                    {[{ k: 'stock', l: 'Stok' }, { k: 'types', l: 'Tipler' }].map(t => (
                        <button key={t.k} onClick={() => setTab(t.k)}
                            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.k ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                            data-testid={`tab-${t.k}`}>{t.l}</button>
                    ))}
                </div>
                {tab === 'stock' && <StockTab />}
                {tab === 'types' && <TypesTab />}
            </div>
        </DashboardLayout>
    );
}

/* ==================== STOCK TAB ==================== */
function StockTab() {
    const [materials, setMaterials] = useState([]);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showScan, setShowScan] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('in_stock');
    const [typeFilter, setTypeFilter] = useState('all');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;

    const fetchData = useCallback(async () => {
        try {
            const [mRes, tRes] = await Promise.all([api.get('/raw-materials'), api.get('/raw-material-types')]);
            setMaterials(mRes.data); setTypes(tRes.data);
        } catch { toast.error('Veriler yuklenemedi'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDelete = async (id) => {
        if (!window.confirm('Bu hammaddeyi silmek istediginizden emin misiniz?')) return;
        try { await api.delete(`/raw-materials/${id}`); toast.success('Silindi'); fetchData(); }
        catch { toast.error('Silinemedi'); }
    };
    const handleUse = async (barcode) => {
        try { const res = await api.post(`/raw-materials/use/${barcode}`); toast.success(res.data.message); fetchData(); return true; }
        catch (err) { toast.error(err.response?.data?.detail || 'Hata'); return false; }
    };

    const filtered = useMemo(() => {
        let list = materials;
        if (statusFilter !== 'all') list = list.filter(m => m.status === statusFilter);
        if (typeFilter !== 'all') list = list.filter(m => m.type_id === typeFilter);
        if (search) { const q = search.toLowerCase(); list = list.filter(m => m.name.toLowerCase().includes(q) || (m.barcode && m.barcode.toLowerCase().includes(q)) || (m.supplier && m.supplier.toLowerCase().includes(q)) || (m.product_name && m.product_name.toLowerCase().includes(q))); }
        return list;
    }, [materials, statusFilter, typeFilter, search]);

    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const inStock = materials.filter(m => m.status === 'in_stock');

    return (
        <div className="space-y-4" data-testid="raw-materials-stock-tab">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                    { label: 'Stokta', value: inStock.length, sub: 'adet', color: 'text-emerald-600' },
                    { label: 'Kullanildi', value: materials.length - inStock.length, sub: 'adet', color: 'text-gray-400' },
                    { label: 'Toplam Agirlik', value: inStock.reduce((s, m) => s + (m.weight_kg || 0), 0).toLocaleString('de-DE', { minimumFractionDigits: 1 }), sub: 'kg', color: 'text-blue-600' },
                    { label: 'Toplam', value: materials.length, sub: 'kayit', color: 'text-gray-600' },
                ].map((s, i) => (
                    <div key={i} className="bg-white rounded-2xl border border-[#E5E7EB] p-4">
                        <p className="text-xs text-[#86868B] mb-1">{s.label}</p>
                        <p className={`text-xl font-bold ${s.color}`}>{s.value} <span className="text-xs font-normal text-[#86868B]">{s.sub}</span></p>
                    </div>
                ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[160px] max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input type="text" placeholder="Ara..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="form-input pl-10" data-testid="raw-material-search" />
                    </div>
                    <div className="flex gap-1">
                        {[{ k: 'in_stock', l: 'Stokta' }, { k: 'used', l: 'Kullanildi' }, { k: 'all', l: 'Tumu' }].map(b => (
                            <button key={b.k} onClick={() => { setStatusFilter(b.k); setPage(1); }} className={`btn text-sm ${statusFilter === b.k ? 'btn-primary' : 'btn-secondary'}`} data-testid={`status-filter-${b.k}`}>{b.l}</button>
                        ))}
                    </div>
                    {types.length > 1 && (
                        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="form-select text-sm h-[38px]" data-testid="type-filter-select">
                            <option value="all">Tum Tipler</option>
                            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    )}
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setShowScan(true)} className="btn btn-secondary text-sm" data-testid="scan-barcode-btn"><ScanLine size={16} /> Barkod Okut</button>
                    <button onClick={() => setShowForm(true)} className="btn btn-primary text-sm" data-testid="add-raw-material-btn"><Plus size={16} /> Yeni Hammadde</button>
                </div>
            </div>

            {loading ? (
                <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-16 bg-[#F3F4F6] rounded-xl animate-pulse" />)}</div>
            ) : filtered.length > 0 ? (
                <div className="data-card">
                    <div className="table-container">
                        <table className="table mobile-cards" data-testid="raw-materials-table">
                            <thead><tr>
                                <th style={{ width: '110px' }}>Barkod</th>
                                <th>Hammadde</th>
                                <th className="mobile-hide" style={{ width: '100px' }}>Tip</th>
                                <th className="mobile-hide" style={{ width: '70px' }}>Olcu</th>
                                <th className="text-right" style={{ width: '80px' }}>Kg</th>
                                <th className="mobile-hide" style={{ width: '110px' }}>Tedarikci</th>
                                <th style={{ width: '85px' }}>Durum</th>
                                <th className="mobile-hide" style={{ width: '90px' }}>Tarih</th>
                                <th className="text-right" style={{ width: '60px' }}>Islem</th>
                            </tr></thead>
                            <tbody>
                                {pageItems.map(m => (
                                    <tr key={m.id} data-testid={`raw-material-row-${m.id}`}>
                                        <td>
                                            <button onClick={() => printBarcodeLabel(m)} className="font-mono text-xs px-2 py-1 bg-[#F5F5F7] rounded-lg hover:bg-[#E5E7EB] transition-colors" title="Barkod Yazdir" data-testid={`print-barcode-${m.id}`}>
                                                <Barcode size={12} className="inline mr-1" />{m.barcode}
                                            </button>
                                        </td>
                                        <td>
                                            <span className="font-medium text-sm">{m.name}</span>
                                            {m.product_name && <span className="block text-[11px] text-[#0284C7]">{m.product_name}{m.variant_name ? ` · ${m.variant_name}` : ''}</span>}
                                            {m.notes && <span className="block text-[11px] text-[#86868B] truncate max-w-[200px]">{m.notes}</span>}
                                        </td>
                                        <td className="mobile-hide"><span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-medium bg-[#F0F9FF] text-[#0284C7]">{m.type_name}</span></td>
                                        <td className="mobile-hide text-sm text-[#374151]">{m.size || '\u2014'}</td>
                                        <td className="text-right font-semibold text-sm">{(m.weight_kg || 0).toLocaleString('de-DE', { minimumFractionDigits: 1 })}</td>
                                        <td className="mobile-hide text-sm text-[#86868B] truncate">{m.supplier || '\u2014'}</td>
                                        <td>
                                            {m.status === 'in_stock'
                                                ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-emerald-50 text-emerald-600"><Package size={11} /> Stokta</span>
                                                : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-gray-100 text-gray-500" title={m.used_by || ''}><CheckCircle size={11} /> Kullanildi</span>}
                                        </td>
                                        <td className="mobile-hide text-xs text-[#86868B]">{new Date(m.status === 'used' && m.used_at ? m.used_at : m.created_at).toLocaleDateString('tr-TR')}</td>
                                        <td className="text-right">
                                            <button onClick={() => handleDelete(m.id)} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10" title="Sil" data-testid={`delete-raw-material-${m.id}`}>
                                                <Trash2 size={15} className="text-[#FF3B30]" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={page} totalPages={Math.ceil(filtered.length / PAGE_SIZE)} onPageChange={setPage} />
                </div>
            ) : (
                <div className="text-center py-16">
                    <Package className="w-16 h-16 text-[#C7C7CC] mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">{materials.length === 0 ? 'Henuz hammadde yok' : 'Filtreyle eslesen kayit yok'}</h3>
                    {materials.length === 0 && <button onClick={() => setShowForm(true)} className="btn btn-primary mt-4"><Plus size={16} /> Yeni Hammadde</button>}
                </div>
            )}

            {showForm && <AddMaterialModal types={types} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchData(); }} />}
            {showScan && <BarcodeScanModal onUse={handleUse} onClose={() => setShowScan(false)} />}
        </div>
    );
}


/* ==================== ADD MATERIAL MODAL ==================== */
function AddMaterialModal({ types, onClose, onSaved }) {
    const [form, setForm] = useState({ type_id: types[0]?.id || '', name: '', weight_kg: '', supplier: '', notes: '', size: '', product_id: '', variant_id: '' });
    const [saving, setSaving] = useState(false);
    const [products, setProducts] = useState([]);
    const [variants, setVariants] = useState([]);
    const [prodSearch, setProdSearch] = useState('');
    const [loadingProducts, setLoadingProducts] = useState(false);

    const selectedType = types.find(t => t.id === form.type_id);
    const isProductMode = selectedType?.selection_mode === 'product';
    const isSizeMode = selectedType?.selection_mode === 'size';
    const availableSizes = selectedType?.sizes || [];

    // Load products when mode is product
    useEffect(() => {
        if (isProductMode && products.length === 0) {
            setLoadingProducts(true);
            api.get('/products').then(res => setProducts(res.data)).catch(() => {}).finally(() => setLoadingProducts(false));
        }
    }, [isProductMode, products.length]);

    // Load variants when product selected
    useEffect(() => {
        if (form.product_id) {
            api.get(`/products/${form.product_id}/variants`).then(res => setVariants(res.data)).catch(() => setVariants([]));
        } else {
            setVariants([]);
        }
    }, [form.product_id]);

    const filteredProducts = useMemo(() => {
        if (!prodSearch) return products;
        const q = prodSearch.toLowerCase();
        return products.filter(p => p.name.toLowerCase().includes(q) || (p.reference && p.reference.toLowerCase().includes(q)));
    }, [products, prodSearch]);

    const handleProductSelect = (p) => {
        setForm(f => ({ ...f, product_id: p.id, name: p.name }));
        setProdSearch('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.type_id || !form.name || !form.weight_kg) return toast.error('Tip, ad ve kg zorunlu');
        setSaving(true);
        try {
            const payload = {
                type_id: form.type_id, name: form.name, weight_kg: parseFloat(form.weight_kg),
                supplier: form.supplier || null, notes: form.notes || null,
                size: isSizeMode ? (form.size || null) : null,
                product_id: isProductMode ? (form.product_id || null) : null,
                variant_id: isProductMode ? (form.variant_id || null) : null,
            };
            const res = await api.post('/raw-materials', payload);
            toast.success(`${res.data.name} eklendi — Barkod: ${res.data.barcode}`);
            onSaved();
        } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-material-modal">
                <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB] sticky top-0 bg-white z-10 rounded-t-2xl">
                    <h2 className="text-lg font-semibold">Yeni Hammadde</h2>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#F5F5F7]"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Type selector */}
                    <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-1">Tip *</label>
                        <select value={form.type_id} onChange={e => setForm(f => ({ ...f, type_id: e.target.value, product_id: '', variant_id: '', size: '', name: '' }))}
                            className="form-select w-full" data-testid="material-type-select">
                            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                    </div>

                    {/* SIZE MODE: Roll / Taban → Size selector */}
                    {isSizeMode && availableSizes.length > 0 && (
                        <div>
                            <label className="block text-xs font-medium text-[#6B7280] mb-1">Olcu *</label>
                            <div className="flex flex-wrap gap-2" data-testid="size-selector">
                                {availableSizes.map(s => (
                                    <button key={s} type="button" onClick={() => setForm(f => ({ ...f, size: s }))}
                                        className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${form.size === s ? 'bg-[#111827] text-white border-[#111827]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#9CA3AF]'}`}
                                        data-testid={`size-option-${s}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* PRODUCT MODE: Kesilmiş → Product picker */}
                    {isProductMode && (
                        <div>
                            <label className="block text-xs font-medium text-[#6B7280] mb-1">Urun Sec *</label>
                            {loadingProducts ? (
                                <div className="text-center py-4"><Loader2 size={20} className="animate-spin mx-auto text-[#86868B]" /></div>
                            ) : form.product_id ? (
                                <div className="flex items-center gap-2 p-3 bg-[#F0F9FF] rounded-xl border border-[#BAE6FD]" data-testid="selected-product">
                                    <div className="flex-1">
                                        <span className="font-medium text-sm">{products.find(p => p.id === form.product_id)?.name || form.name}</span>
                                        <span className="text-xs text-[#86868B] ml-2">{products.find(p => p.id === form.product_id)?.reference || ''}</span>
                                    </div>
                                    <button type="button" onClick={() => setForm(f => ({ ...f, product_id: '', variant_id: '', name: '' }))} className="p-1 rounded hover:bg-white/50"><X size={16} /></button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                        <input type="text" value={prodSearch} onChange={e => setProdSearch(e.target.value)}
                                            placeholder="Urun adi veya referans ara..." className="form-input pl-10 w-full" data-testid="product-search-input" />
                                    </div>
                                    <div className="max-h-48 overflow-y-auto border border-[#E5E7EB] rounded-xl" data-testid="product-list">
                                        {filteredProducts.slice(0, 20).map(p => (
                                            <button key={p.id} type="button" onClick={() => handleProductSelect(p)}
                                                className="w-full text-left px-3 py-2.5 hover:bg-[#F5F5F7] border-b border-[#F3F4F6] last:border-0 transition-colors"
                                                data-testid={`product-option-${p.id}`}>
                                                <span className="text-sm font-medium">{p.name}</span>
                                                {p.reference && <span className="text-xs text-[#86868B] ml-2">({p.reference})</span>}
                                            </button>
                                        ))}
                                        {filteredProducts.length === 0 && <p className="text-sm text-[#86868B] p-3 text-center">Urun bulunamadi</p>}
                                    </div>
                                </div>
                            )}

                            {/* Variant selector */}
                            {form.product_id && variants.length > 0 && (
                                <div className="mt-3">
                                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Varyant</label>
                                    <select value={form.variant_id} onChange={e => setForm(f => ({ ...f, variant_id: e.target.value }))}
                                        className="form-select w-full" data-testid="variant-select">
                                        <option value="">Varyantsiz</option>
                                        {variants.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Name */}
                    <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-1">Ad *</label>
                        <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="orn: Roll A-001" className="form-input w-full" data-testid="material-name-input" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-[#6B7280] mb-1">Agirlik (kg) *</label>
                            <input type="number" step="0.1" value={form.weight_kg} onChange={e => setForm(f => ({ ...f, weight_kg: e.target.value }))}
                                placeholder="250" className="form-input w-full" data-testid="material-weight-input" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-[#6B7280] mb-1">Tedarikci</label>
                            <input type="text" value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
                                placeholder="ABC Kagit" className="form-input w-full" data-testid="material-supplier-input" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-1">Not</label>
                        <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                            className="form-input w-full" data-testid="material-notes-input" />
                    </div>

                    <button type="submit" disabled={saving} className="btn btn-primary w-full" data-testid="save-material-btn">
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                        {saving ? 'Kaydediliyor...' : 'Kaydet & Barkod Olustur'}
                    </button>
                </form>
            </div>
        </div>
    );
}


/* ==================== BARCODE SCAN MODAL ==================== */
function BarcodeScanModal({ onUse, onClose }) {
    const [input, setInput] = useState('');
    const [result, setResult] = useState(null);
    const [processing, setProcessing] = useState(false);

    const handleScan = async (e) => {
        e.preventDefault();
        const code = input.trim().toUpperCase();
        if (!code) return;
        setProcessing(true);
        const ok = await onUse(code);
        setResult(ok ? { success: true, code } : { success: false, code });
        setProcessing(false);
        if (ok) setInput('');
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()} data-testid="barcode-scan-modal">
                <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold">Barkod Okut — Stoktan Dus</h2>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#F5F5F7]"><X size={20} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <form onSubmit={handleScan} className="flex gap-2">
                        <input type="text" value={input} onChange={e => setInput(e.target.value)}
                            placeholder="Barkod okutun veya girin..." autoFocus className="form-input flex-1" data-testid="barcode-input" />
                        <button type="submit" disabled={processing} className="btn btn-primary" data-testid="use-barcode-btn">
                            {processing ? <Loader2 size={16} className="animate-spin" /> : <ScanLine size={16} />}
                        </button>
                    </form>
                    {result && (
                        <div className={`p-3 rounded-xl text-sm font-medium ${result.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`} data-testid="barcode-result">
                            {result.success ? `${result.code} stoktan dusuruldu` : `${result.code} islenemedi`}
                        </div>
                    )}
                    <p className="text-xs text-[#86868B] text-center">Barkod okuyucu ile okutun veya manuel girin</p>
                </div>
            </div>
        </div>
    );
}


/* ==================== TYPES TAB ==================== */
function TypesTab() {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingType, setEditingType] = useState(null);

    const fetchTypes = useCallback(async () => {
        try { setTypes((await api.get('/raw-material-types')).data); }
        catch { toast.error('Tipler yuklenemedi'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { fetchTypes(); }, [fetchTypes]);

    const handleDelete = async (id) => {
        if (!window.confirm('Bu tipi silmek istediginizden emin misiniz?')) return;
        try { await api.delete(`/raw-material-types/${id}`); toast.success('Tip silindi'); fetchTypes(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Silinemedi'); }
    };

    return (
        <div className="space-y-4" data-testid="raw-material-types-tab">
            <div className="flex justify-between items-center">
                <p className="text-sm text-[#6B7280]">{types.length} tip tanimli</p>
                <button onClick={() => { setEditingType(null); setShowForm(true); }} className="btn btn-primary text-sm" data-testid="add-type-btn"><Plus size={14} /> Yeni Tip</button>
            </div>

            {loading ? (
                <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-12 bg-[#F3F4F6] rounded-xl animate-pulse" />)}</div>
            ) : types.length > 0 ? (
                <div className="data-card">
                    <div className="table-container">
                        <table className="table" data-testid="types-table">
                            <thead><tr><th>Tip Adi</th><th>Mod</th><th className="mobile-hide">Olculer</th><th className="text-right">Islem</th></tr></thead>
                            <tbody>
                                {types.map(t => (
                                    <tr key={t.id} data-testid={`type-row-${t.id}`}>
                                        <td className="font-medium text-sm">{t.name}</td>
                                        <td>
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-medium ${t.selection_mode === 'product' ? 'bg-purple-50 text-purple-600' : 'bg-amber-50 text-amber-600'}`}>
                                                {t.selection_mode === 'product' ? 'Urun Secimi' : 'Olcu Secimi'}
                                            </span>
                                        </td>
                                        <td className="mobile-hide text-xs text-[#86868B]">
                                            {t.selection_mode === 'size' && t.sizes?.length > 0 ? t.sizes.join(', ') : '\u2014'}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-0.5">
                                                <button onClick={() => { setEditingType(t); setShowForm(true); }} className="p-1.5 rounded-lg hover:bg-[#F5F5F7]" title="Duzenle" data-testid={`edit-type-${t.id}`}>
                                                    <Edit2 size={15} className="text-[#007AFF]" />
                                                </button>
                                                <button onClick={() => handleDelete(t.id)} className="p-1.5 rounded-lg hover:bg-[#FF3B30]/10" title="Sil" data-testid={`delete-type-${t.id}`}>
                                                    <Trash2 size={15} className="text-[#FF3B30]" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="text-center py-12">
                    <Settings2 className="w-12 h-12 text-[#C7C7CC] mx-auto mb-3" />
                    <p className="text-[#86868B]">Henuz tip tanimlanmamis</p>
                </div>
            )}

            {showForm && <TypeFormModal type={editingType} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchTypes(); }} />}
        </div>
    );
}


/* ==================== TYPE FORM MODAL ==================== */
function TypeFormModal({ type, onClose, onSaved }) {
    const [name, setName] = useState(type?.name || '');
    const [mode, setMode] = useState(type?.selection_mode || 'size');
    const [sizesText, setSizesText] = useState((type?.sizes || []).join(', '));
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error('Tip adi zorunlu');
        setSaving(true);
        const sizes = mode === 'size' ? sizesText.split(',').map(s => s.trim()).filter(Boolean) : [];
        const payload = { name: name.trim(), selection_mode: mode, sizes };
        try {
            if (type?.id) {
                await api.put(`/raw-material-types/${type.id}`, payload);
                toast.success('Tip guncellendi');
            } else {
                await api.post('/raw-material-types', payload);
                toast.success('Tip eklendi');
            }
            onSaved();
        } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()} data-testid="type-form-modal">
                <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
                    <h2 className="text-lg font-semibold">{type ? 'Tip Duzenle' : 'Yeni Tip'}</h2>
                    <button onClick={onClose} className="p-1 rounded-lg hover:bg-[#F5F5F7]"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-1">Tip Adi *</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="orn: Roll, Taban Kagidi" className="form-input w-full" data-testid="type-name-input" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#6B7280] mb-2">Secim Modu</label>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setMode('size')}
                                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${mode === 'size' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-white text-[#6B7280] border-[#E5E7EB]'}`}
                                data-testid="mode-size-btn">
                                Olcu Secimi
                                <span className="block text-[10px] mt-0.5 opacity-70">Roll, Taban icin</span>
                            </button>
                            <button type="button" onClick={() => setMode('product')}
                                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium border transition-all ${mode === 'product' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-white text-[#6B7280] border-[#E5E7EB]'}`}
                                data-testid="mode-product-btn">
                                Urun Secimi
                                <span className="block text-[10px] mt-0.5 opacity-70">Kesilmis icin</span>
                            </button>
                        </div>
                    </div>
                    {mode === 'size' && (
                        <div>
                            <label className="block text-xs font-medium text-[#6B7280] mb-1">Olculer (virgul ile)</label>
                            <input type="text" value={sizesText} onChange={e => setSizesText(e.target.value)}
                                placeholder="4, 7, 7.5, 8, 12, Doner Box" className="form-input w-full" data-testid="sizes-input" />
                            <p className="text-[11px] text-[#9CA3AF] mt-1">Virgul ile ayirarak olculeri girin</p>
                        </div>
                    )}
                    <button type="submit" disabled={saving} className="btn btn-primary w-full" data-testid="save-type-btn">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        {saving ? 'Kaydediliyor...' : type ? 'Guncelle' : 'Kaydet'}
                    </button>
                </form>
            </div>
        </div>
    );
}


/* ==================== BARCODE LABEL PRINT ==================== */
function printBarcodeLabel(material) {
    const w = window.open('', '_blank', 'width=400,height=300');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
body{font-family:sans-serif;padding:8mm;text-align:center;margin:0}
.label{border:2px solid #000;padding:6mm 8mm;display:inline-block;min-width:70mm}
.name{font-size:13px;font-weight:bold;margin:2mm 0}
.barcode-img{margin:3mm auto;display:block}
.code{font-size:14px;font-weight:bold;letter-spacing:3px;font-family:monospace;margin:2mm 0}
.info{font-size:9px;margin:1mm 0;color:#333}
</style></head><body>
<div class="label">
<div class="name">${material.name}</div>
${material.size ? `<div class="info" style="font-size:11px;font-weight:bold">${material.type_name} - ${material.size}</div>` : `<div class="info">${material.type_name}</div>`}
${material.product_name ? `<div class="info" style="color:#0284C7">${material.product_name}${material.variant_name ? ' · ' + material.variant_name : ''}</div>` : ''}
<img class="barcode-img" src="https://barcodeapi.org/api/128/${material.barcode}" height="50" onerror="this.style.display='none'" />
<div class="code">${material.barcode}</div>
<div class="info">${(material.weight_kg || 0).toLocaleString('de-DE', { minimumFractionDigits: 1 })} kg${material.supplier ? ' | ' + material.supplier : ''}</div>
<div class="info">${new Date(material.created_at).toLocaleDateString('tr-TR')}</div>
</div>
<script>setTimeout(()=>print(),800)</script></body></html>`);
    w.document.close();
}
